/**
 * Soft≠Live Edge helpers — normalize Live device / session / sibling envelopes.
 * Never invent lane ms/mbps, Soft node labels, or Soft session byte counters.
 */

import { boardHeadingOf, boardLabelSay } from "./operator-say.js";

/** @param {unknown} envelope GET /devices */
export function edgeDevicesOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.devices || (Array.isArray(root) ? root : []);
  if (!Array.isArray(items)) return [];
  const rows = items
    .map((d) => {
      if (!d || typeof d !== "object") return null;
      const id = String(d.device_id || d.id || "").trim();
      if (!id) return null;
      /* Soft≠Live: Live registry uses is_online / fw / board_profile — never invent. */
      const online = d.is_online === true || d.online === true || d.presence === "online"
        || String(d.status || "").toLowerCase() === "online";
      const fw = String(d.fw || d.fw_version || d.firmware || d.base_version || "").trim();
      const kind = String(
        d.board_profile || d.product_hw_code || d.hw_code_resolved || d.chip_target
        || d.kind || d.device_kind || d.class || "",
      ).trim();
      let net = String(
        d.net_type || d.link || (typeof d.net_health === "string" ? d.net_health : "") || "",
      ).trim();
      if (net === "[object Object]") net = "";
      const hbAge = Number(d.last_heartbeat_age_s);
      return {
        id,
        label: boardLabelSay(d, id),
        /* Soft≠Live P1-FIELD-1: Group ≠ Zone — never collapse Site into Group caption. */
        group: String(d.notification_group || "").trim(),
        zone: String(d.zone || "").trim(),
        net,
        online: !!online,
        fw,
        kind,
        hbAgeS: Number.isFinite(hbAge) && hbAge >= 0 ? hbAge : null,
        netHealth: typeof d.net_health === "string" ? String(d.net_health).trim() : "",
        raw: d,
      };
    })
    .filter(Boolean);
  /* One board · one card. Duplicate device_id rows (e.g. two camera names) stay one unit. */
  const seen = new Set();
  const unique = [];
  for (const d of rows) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    unique.push(d);
  }
  return unique;
}

/** @param {unknown} envelope GET /devices/{id}/live-sessions */
export function liveSessionsOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.sessions || [];
  if (!Array.isArray(items)) return [];
  return items
    .map((s) => {
      if (!s || typeof s !== "object") return null;
      const id = String(s.session_id || s.id || "").trim();
      if (!id) return null;
      const left = Number(s.ttl_remaining_s ?? s.left_s ?? s.seconds_remaining ?? s.expires_in_s);
      return {
        id,
        reason: String(s.operator_reason || s.reason || "").trim() || "—",
        by: String(s.opened_by || s.actor || s.username || "").trim() || "—",
        leftS: Number.isFinite(left) && left >= 0 ? left : null,
        camera: String(s.camera_id || s.camera_hint || "").trim(),
        status: String(s.status || s.state || "open").trim(),
        mediaPlayed: s.media_played === true,
        hasDigest: s.has_digest === true,
        raw: s,
      };
    })
    .filter(Boolean);
}

/** @param {unknown} envelope GET /devices/{id}/siblings-preview */
export function siblingsOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const targets = root.targets || root.items || [];
  return {
    ok: root.ok === true,
    zone: String(root.zone || "").trim(),
    group: String(root.notification_group || "").trim(),
    fanout: !!root.fanout_enabled,
    targetCount: Number(root.target_count) || (Array.isArray(targets) ? targets.length : 0),
    eligible: Number(root.eligible_total) || 0,
    targets: Array.isArray(targets)
      ? targets.map((t) => {
        if (typeof t === "string") return { id: t, label: t };
        if (!t || typeof t !== "object") return null;
        const id = String(t.device_id || t.id || "").trim();
        if (!id) return null;
        return {
          id,
          label: String(t.label || t.display_label || "").trim() || boardHeadingOf(id),
          raw: t,
        };
      }).filter(Boolean)
      : [],
    raw: root,
  };
}

/** @param {unknown} envelope GET /devices/capabilities */
export function capabilityCatalogOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const catalog = root.catalog || [];
  const items = root.items || [];
  return {
    catalog: Array.isArray(catalog) ? catalog.map((c) => String(c).trim()).filter(Boolean) : [],
    items: Array.isArray(items) ? items : [],
    count: Number(root.count) || 0,
    persistence: String(root.persistence || "").trim(),
    raw: root,
  };
}

/** @param {unknown} envelope GET /devices/{id}/scheduled-jobs */
export function scheduledJobsOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.jobs || [];
  if (!Array.isArray(items)) return [];
  return items
    .map((j) => {
      if (!j || typeof j !== "object") return null;
      const id = String(j.job_id || j.id || "").trim();
      if (!id) return null;
      const cmd = String(j.cmd || j.command || "").trim();
      const at = j.execute_at_ts ?? j.execute_at ?? j.at_ts ?? null;
      const title = String(j.title || j.name || cmd || id).trim() || id;
      return {
        id,
        title,
        cmd: cmd || "—",
        status: String(j.status || j.state || "—").trim(),
        executeAt: at != null && Number.isFinite(Number(at)) ? Number(at) : null,
        raw: j,
      };
    })
    .filter(Boolean);
}

/** Pure: compact Live board-telemetry fields (never invent sensors). */
export function boardTelemetryViewOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : null;
  if (!root) return null;
  const keys = Object.keys(root).slice(0, 24);
  const payload = root.payload && typeof root.payload === "object" ? root.payload : {};
  const net =
    (payload.net && typeof payload.net === "object" && payload.net) ||
    (payload.network && typeof payload.network === "object" && payload.network) ||
    {};
  return {
    profile: String(root.board_profile || root.profile_id || root.profile || "").trim() || "—",
    temp: root.temp_c ?? root.temperature_c ?? root.die_temp_c ?? null,
    heap: root.heap_free ?? root.free_heap ?? root.heap ?? null,
    uptime: root.uptime_s ?? root.uptime ?? null,
    fresh: root.fresh === true,
    ageS: root.age_s != null && Number.isFinite(Number(root.age_s)) ? Number(root.age_s) : null,
    silicon: Array.isArray(root.silicon) ? root.silicon : [],
    net: {
      ethIp: _liveStr(net.eth_ip || net.lan_ip || net.ip || net.ipv4),
      wifiIp: _liveStr(net.wifi_ip || net.sta_ip),
      ssid: _liveStr(net.ssid || net.wifi_ssid),
      ethUp: _liveBool(net.eth_up ?? net.link_up),
      wifiUp: _liveBool(net.wifi_up ?? net.sta_up),
    },
    keys,
    raw: root,
  };
}

function _liveStr(v) {
  const s = String(v == null ? "" : v).trim();
  return s || null;
}

function _liveBool(v) {
  if (v === true || v === false) return v;
  if (v === 1 || v === "1" || v === "up" || v === "online") return true;
  if (v === 0 || v === "0" || v === "down" || v === "offline") return false;
  return null;
}

/** Pure: TF card envelope Soft≠Live (never invent present/mounted). */
export function storageCardViewOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : null;
  if (!root) return null;
  return {
    fresh: root.fresh === true,
    present: root.present === true,
    mounted: root.mounted === true,
    fs: String(root.fs || "").trim() || "—",
    capacityMb: root.capacity_mb != null && Number.isFinite(Number(root.capacity_mb))
      ? Number(root.capacity_mb) : null,
    freeMb: root.free_mb != null && Number.isFinite(Number(root.free_mb))
      ? Number(root.free_mb) : null,
    health: String(root.health || "unknown").trim() || "unknown",
    ageS: root.age_s != null && Number.isFinite(Number(root.age_s)) ? Number(root.age_s) : null,
  };
}

/** Pure: physical ports + live bind (IP only when reported). */
export function boardPortsViewOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.ports || [];
  if (!Array.isArray(items)) return [];
  return items
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const id = String(p.port_id || p.id || "").trim();
      if (!id) return null;
      const live = p.live && typeof p.live === "object" ? p.live : {};
      return {
        id,
        label: String(p.label || id).trim() || id,
        kind: String(p.kind || "").trim(),
        lanes: String(p.lanes || "").trim(),
        link: _liveBool(live.link_up ?? live.up ?? live.present),
        ip: _liveStr(live.ip || live.ipv4 || live.addr),
        detail: _liveStr(live.state || live.mode || live.speed),
        live,
      };
    })
    .filter(Boolean);
}

/**
 * Pure: pin wake / sleep / rail from Live probe + binding.
 * Never invent "wake" when probe and binding are empty.
 */
export function pinWakeOf(seat) {
  if (!seat || typeof seat !== "object") return "sleep";
  if (seat.assignable === false || String(seat.seat_class || "").toLowerCase() === "power") {
    return "rail";
  }
  const probe = String(seat.probe || seat.bias || seat.pad_state || "").toLowerCase();
  if (probe === "floating") return "sleep";
  if (
    probe === "held_low" ||
    probe === "held_high" ||
    probe === "unstable" ||
    probe === "driven" ||
    probe === "active"
  ) {
    return "wake";
  }
  const st = String(
    (seat.binding && seat.binding.state) || seat.state || "",
  ).toLowerCase();
  if (st === "verified" || st === "claimed" || st === "pending_confirm") return "wake";
  if (seat.gpio != null && Number.isFinite(Number(seat.gpio))) return "mapped";
  return "sleep";
}

/** Measured-on vs honest-hold vs fail. Never promote parked/unprobed to on. */
export function loopToneOf(key, value) {
  const k = String(key || "").trim();
  const v = String(value || "").trim();
  if (!k || !v) return "off";
  if (
    v === "fail" ||
    v === "down" ||
    v === "query_fail" ||
    v === "sync_fail"
  ) {
    return "off";
  }
  if (
    v === "parked" ||
    v === "not_live" ||
    v === "HOLD_NO_SEAL" ||
    v === "unprobed" ||
    v === "compiled_out" ||
    v === "idle" ||
    v === "dry" ||
    v === "absent" ||
    v === "empty" ||
    v === "sb_off" ||
    v === "fe_off" ||
    v === "off" ||
    v === "no_i2c" ||
    v === "no_sccb" ||
    v === "none"
  ) {
    return "hold";
  }
  return "on";
}

/**
 * Pure: GET /devices/{id}/board-loop. Never invent a closed loop or AI Live.
 * @param {object|null} envelope
 */
export function boardLoopViewOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : null;
  if (!root) return null;
  const raw = root.loops && typeof root.loops === "object" ? root.loops : {};
  const items = Object.keys(raw)
    .map((id) => {
      const value = String(raw[id] || "").trim();
      if (!id || !value) return null;
      return { id, value, tone: loopToneOf(id, value) };
    })
    .filter(Boolean)
    .sort((a, b) => a.id.localeCompare(b.id));
  const roles = root.verified_roles && typeof root.verified_roles === "object"
    ? root.verified_roles
    : {};
  const caps = Array.isArray(root.port_capabilities) ? root.port_capabilities : [];
  const lanRaw = root.lan && typeof root.lan === "object" ? root.lan : {};
  const lanPath = String(lanRaw.path || raw.lan || "").trim();
  return {
    items,
    pinMapped: Number.isFinite(Number(root.pin_mapped)) ? Number(root.pin_mapped) : 0,
    pinReported: root.pin_reported === true,
    portsLive: root.ports_live === true,
    interfacesN: Number.isFinite(Number(root.interfaces_n)) ? Number(root.interfaces_n) : 0,
    skillMqttClosed: root.skill_mqtt_closed === true,
    aiParked: root.ai_parked !== false,
    portCaps: caps,
    verifiedRoles: roles,
    lan: {
      path: lanPath,
      prefix: String(lanRaw.prefix || "").trim(),
      gwMac: String(lanRaw.gw_mac || "").trim(),
      bssid: String(lanRaw.bssid || "").trim(),
      lock: String(lanRaw.lock || "").trim(),
      lldp: String(lanRaw.lldp || "").trim(),
      cdp: String(lanRaw.cdp || "").trim(),
      lldpChassis: String(lanRaw.lldp_chassis || "").trim(),
      lldpPort: String(lanRaw.lldp_port || "").trim(),
      cdpId: String(lanRaw.cdp_id || "").trim(),
      nbrN: String(lanRaw.nbr_n != null ? lanRaw.nbr_n : "").trim(),
    },
  };
}

/** Pure: one loop value from boardLoopViewOf. Empty when absent Soft≠ invent. */
export function loopValueOf(view, id) {
  if (!view || !Array.isArray(view.items)) return "";
  const key = String(id || "").trim();
  if (!key) return "";
  const row = view.items.find((x) => x && x.id === key);
  return row ? String(row.value || "").trim() : "";
}

/**
 * Pure honesty facts for Edge wave desks. Soft≠ GPS / mesh_live / Vision Live / W3.
 * @param {ReturnType<typeof boardLoopViewOf>|null} view
 */
export function edgeWaveHonestyOf(view) {
  const locate = loopValueOf(view, "locate");
  const w1 = loopValueOf(view, "w1");
  const cam = loopValueOf(view, "cam");
  const w3 = loopValueOf(view, "w3");
  const mesh = loopValueOf(view, "mesh");
  const tf = loopValueOf(view, "tf");
  const skill = loopValueOf(view, "skill");
  const w4 = loopValueOf(view, "w4");
  const psram = loopValueOf(view, "psram");
  return {
    locate,
    locateSealed: locate === "sealed",
    locateHold: !locate || locate === "HOLD_NO_SEAL",
    w1,
    remuxActive: w1 === "remux",
    cam,
    jpegOk: cam === "jpeg_ok",
    w3,
    aiParked: !view || view.aiParked !== false,
    mesh,
    meshLiveClaim: mesh === "mesh_live" || mesh === "live",
    tf,
    tfMounted: tf === "mounted",
    skill,
    skillMqttClosed: !!(view && view.skillMqttClosed),
    w4,
    psram,
    floor: loopValueOf(view, "floor"),
    ota: loopValueOf(view, "ota"),
    mtls: loopValueOf(view, "mtls"),
    fe: loopValueOf(view, "fe"),
    watch: loopValueOf(view, "watch"),
    mqtt: loopValueOf(view, "mqtt"),
    c6: loopValueOf(view, "c6"),
    eth: loopValueOf(view, "eth"),
    wifi: loopValueOf(view, "wifi"),
    usb: loopValueOf(view, "usb"),
    dsi: loopValueOf(view, "dsi"),
    csi: loopValueOf(view, "csi"),
    c6fw: loopValueOf(view, "c6fw"),
    ftm: loopValueOf(view, "ftm"),
    gps: loopValueOf(view, "gps"),
    p4rev: loopValueOf(view, "p4rev"),
    w9: loopValueOf(view, "w9"),
    spk: loopValueOf(view, "spk"),
    id: loopValueOf(view, "id"),
    sync: loopValueOf(view, "sync"),
    flash: loopValueOf(view, "flash"),
    cmd: loopValueOf(view, "cmd"),
    uart: loopValueOf(view, "uart"),
    i2c: loopValueOf(view, "i2c"),
    mic: loopValueOf(view, "mic"),
    rtc: loopValueOf(view, "rtc"),
    lanPath: (view && view.lan && view.lan.path) || loopValueOf(view, "lan") || "",
    lanPrefix: (view && view.lan && view.lan.prefix) || "",
    lanBssid: (view && view.lan && view.lan.bssid) || "",
    lanGwMac: (view && view.lan && view.lan.gwMac) || "",
    lanLock: (view && view.lan && view.lan.lock) || "",
    lanLldp: (view && view.lan && view.lan.lldp) || "",
    lanCdp: (view && view.lan && view.lan.cdp) || "",
    lanLldpWho: ((view && view.lan && view.lan.lldpChassis) || "")
      + ((view && view.lan && view.lan.lldpPort) ? (" / " + view.lan.lldpPort) : ""),
    lanCdpWho: (view && view.lan && view.lan.cdpId) || "",
    lanNbrN: (view && view.lan && view.lan.nbrN) || "",
  };
}

/** Dual locate: registry map pin ≠ firmware emit. Soft≠ GPS / GeoIP. */
export function locateDualHonestyOf(facts, device) {
  const edge = String((facts && facts.locate) || (device && device.location_edge_loop) || "").trim();
  const src = String((device && device.location_source) || "").trim();
  const hasCoords = !!(device && device.map_lat != null && device.map_lng != null);
  const split = hasCoords && edge === "HOLD_NO_SEAL";
  const l2 = src.toUpperCase() === "L2_BROTHER";
  let say;
  if (l2 && hasCoords) {
    say = "same-LAN brother · not metres · Soft≠ GeoIP · Soft≠ prefix-only · edge " + (edge || "unread");
  } else if (split) {
    say = "Registry " + (src || "coords") + " · edge " + edge + " · Soft≠ treat map pin as firmware emit / GPS";
  } else if (edge) {
    say = "edge " + edge + (src ? " · registry " + src : "");
  } else if (src) {
    say = "registry " + src;
  } else {
    say = "No locate fact yet";
  }
  const lock = String((device && device.location_lan_lock) || (facts && facts.lanLock) || "").trim();
  if (lock === "drift") {
    say = (say || "locate") + " · LAN lock drift Soft≠ clear install seal Soft≠ metres";
  }
  return {
    edge,
    registry: src,
    hasCoords,
    split,
    lat: hasCoords ? device.map_lat : null,
    lng: hasCoords ? device.map_lng : null,
    say,
  };
}

/** Host μDock lease ≠ P4 remux pump. Soft≠ invent stream bytes. */
export function udockLiveHonestyOf(facts, sessions) {
  const rows = Array.isArray(sessions) ? sessions : [];
  const leaseN = rows.length;
  const mediaN = rows.filter((s) => s && s.mediaPlayed === true).length;
  const openN = rows.filter((s) => {
    const st = String((s && s.status) || "").toLowerCase();
    return st === "active" || st === "open" || st === "requested" || st === "dialing";
  }).length;
  const remux = !!(facts && facts.remuxActive);
  const w1 = String((facts && facts.w1) || "").trim();
  const split = leaseN > 0 && !remux;
  return {
    leaseN,
    openN,
    mediaN,
    remux,
    w1,
    split,
    say: remux
      ? ("board-loop w1=remux · " + leaseN + " lease(s) · Soft≠ invent decode")
      : (split
        ? (leaseN + " host lease(s) · media_played=" + mediaN + " · board-loop w1=" + (w1 || "idle") + " · Soft≠ treat lease as remux playing")
        : ("no remux · " + leaseN + " lease(s)")),
  };
}

/** Closed / expired / past TTL never lights Cinema Live. Soft≠ historic media_played. */
export function cinemaSessionOpenOf(session) {
  if (!session || typeof session !== "object") return false;
  const st = String(session.state || session.status || "").toLowerCase();
  if (st === "closed" || st === "expired") return false;
  let exp = Number(session.expires_at || session.expiresAt || 0);
  if (Number.isFinite(exp) && exp > 0) {
    if (exp > 1e12) exp = exp / 1000;
    if (exp < Date.now() / 1000) return false;
  }
  return true;
}

export function cinemaSessionLiveOf(session) {
  if (!cinemaSessionOpenOf(session)) return false;
  return session.viewable === true && session.media_played === true;
}

export function cinemaCardLiveOf(card) {
  /* Live Pixel Rule: live_preview is a ticket, not LIVE. media_played only. */
  if (!card || typeof card !== "object") return false;
  if (card.soft_stamp || card.soft_demo || card.soft_neq_live) return false;
  const session = card.session && typeof card.session === "object" ? card.session : {};
  return cinemaSessionLiveOf(session);
}

export function fmtLeft(s) {
  if (s == null || !Number.isFinite(s)) return "—";
  const n = Math.max(0, Math.floor(s));
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}
